import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Card,
  Alert,
  Table,
  InputNumber,
  AutoComplete,
  Tag,
  Tooltip,
  message,
  Divider,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  WarningOutlined,
  HomeOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SwapOutlined,
  BoxPlotOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { InternalTransfer, TransferItem as BaseTransferItem, TransferStatus } from '../pages/InternalTransferPage';
import apiService from '../../../services/apiService';

// 本地擴展的轉移項目接口，確保類型安全
interface TransferItem extends BaseTransferItem {
  quantity: number;
  currentCustomer?: string;
  targetCustomer?: string;
  currentWarehouse?: string;
  targetWarehouse?: string;
}

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface Warehouse {
  value: string;
  label: string;
  locations: string[];
  id?: string;
  code?: string;
}

interface Product {
  id: string;
  name: string;
  nameCN: string;
  sku: string;
  availableStock: Record<string, number>;
  unit: string;
  currentLocation?: {
    warehouse: string;
    location: string;
    customer?: string;
  };
  alternateSkuList: string[];
}

interface Customer {
  id: string;
  name: string;
  code: string;
}

interface InternalTransferFormProps {
  visible: boolean;
  transfer?: InternalTransfer | null;
  warehouses: Warehouse[];
  products: Product[];
  customers?: Customer[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

const InternalTransferForm: React.FC<InternalTransferFormProps> = ({
  visible,
  transfer,
  warehouses,
  products,
  customers = [],
  onSubmit,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [items, setItems] = useState<TransferItem[]>([]);
  const [localCustomers, setLocalCustomers] = useState<Customer[]>([]);
  const [searchProducts, setSearchProducts] = useState<Product[]>([]);
  const [localWarehouses, setLocalWarehouses] = useState<Warehouse[]>([]);
  // 注意：下面的狀態變量在新的工作流程中已不再使用，但為了兼容性暫時保留
  const [fromWarehouse, setFromWarehouse] = useState<string>('');
  const [toWarehouse, setToWarehouse] = useState<string>('');
  const [fromLocations, setFromLocations] = useState<string[]>([]);
  const [toLocations, setToLocations] = useState<string[]>([]);

  // 初始化表單數據
  useEffect(() => {
    if (visible) {
      if (transfer) {
        // 編輯模式
        form.setFieldsValue({
          title: transfer.title,
          priority: transfer.priority,
          reason: transfer.reason,
          notes: transfer.notes,
          fromWarehouse: transfer.fromWarehouse || undefined,
          toWarehouse: transfer.toWarehouse || undefined,
        });
        // 將後端字段映射到表單內部結構，確保回顯
        const mapped = (transfer.items || []).map((it: any) => ({
          id: String(it.id),
          productId: String(it.productId || ''),
          productName: it.productName || '',
          productNameCN: it.productNameCN || '',
          sku: it.sku || '',
          quantity: Number(it.quantity) || 1,
          availableQuantity: (it.availableQuantity !== undefined && it.availableQuantity !== null) ? Number(it.availableQuantity) : (undefined as any),
          unit: it.unit || '個',
          fromLocation: it.fromLocation || '',
          toLocation: it.toLocation || '',
          isQuantityValid: it.isQuantityValid !== false,
          currentCustomer: it.currentCustomer || '',
          targetCustomer: it.targetCustomer || it.targetCustomerId || '',
          currentWarehouse: it.currentWarehouse || '',
          targetWarehouse: it.targetWarehouse || transfer.toWarehouse || '',
          remark: it.remark || '',
        }));
        setItems(mapped);
      } else {
        // 新增模式
        form.resetFields();
        setItems([]);
      }
      // 清除舊的倉庫狀態
      setFromWarehouse('');
      setToWarehouse('');
      setFromLocations([]);
      setToLocations([]);
    }
  }, [visible, transfer, form, warehouses]);

  // 動態載入的庫位快取（key：warehouseId -> locations[]）
  const [dynamicLocationsByWarehouse, setDynamicLocationsByWarehouse] = useState<Record<string, string[]>>({});

  // 懶載入客戶（若父層未提供）
  const ensureCustomersLoaded = async () => {
    try {
      if ((customers?.length || 0) > 0 || (localCustomers?.length || 0) > 0) return;
      const resp: any = await apiService.getCustomersV2();
      const rows: any[] = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
      const mapped: Customer[] = rows.map((c: any) => ({
        id: String(c.id || c.customerId),
        name: c.name || c.customerName || '-',
        code: c.code || c.customerCode || '-'
      }));
      setLocalCustomers(mapped);
    } catch (_) {
      setLocalCustomers([]);
    }
  };

  // 懶載入倉庫（若父層未提供）
  const ensureWarehousesLoaded = async () => {
    try {
      if ((warehouses?.length || 0) > 0 || (localWarehouses?.length || 0) > 0) return;
      const companyId = localStorage.getItem('companyId') || localStorage.getItem('currentCompanyId') || undefined;
      const resp: any = await apiService.getWarehouses(companyId ? { companyId } : undefined);
      const rows: any[] = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
      const mapped: Warehouse[] = rows.map((w: any) => ({
        value: String(w.id || w.warehouseId || w.code),
        label: w.name || w.warehouseName || w.label || '-',
        locations: Array.isArray(w.locations) ? w.locations : [],
        id: String(w.id || w.warehouseId || ''),
        code: w.code || w.warehouseCode || undefined,
      }));
      setLocalWarehouses(mapped);
    } catch (_) {
      setLocalWarehouses([]);
    }
  };

  // 產品即時搜尋（使用現有後端 API）
  const searchProductsByKeyword = async (keyword?: string) => {
    try {
      const companyId = localStorage.getItem('companyId') || localStorage.getItem('currentCompanyId') || undefined;
      let resp: any;
      try {
        resp = await apiService.getProductsV2(keyword ? { search: keyword, companyId } : (companyId ? { companyId } : undefined));
      } catch (_) {
        // v2 不可用時，回退舊版 /products
        resp = await apiService.getProducts(keyword ? { search: keyword, companyId } : (companyId ? { companyId } : undefined));
      }
      const rows: any[] = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.products)
        ? resp.products
        : Array.isArray(resp)
        ? resp
        : [];
      const mapped: Product[] = rows.map((p: any) => ({
        id: String(p.id || p.productId || p.itemId || p.code || p.itemCode),
        name: p.name || p.productName || p.itemName || '-',
        nameCN: p.nameCN || p.productNameCN || p.cnName || p.itemName || '-',
        sku: p.sku || p.productSku || p.itemCode || p.code || '-',
        availableStock: p.availableStock || {},
        unit: p.unit || p.uom || '個',
        currentLocation: {
          warehouse: p.currentLocation?.warehouse || '-',
          location: p.currentLocation?.location || '-',
          customer: p.currentLocation?.customer || '-',
        },
        alternateSkuList: p.alternateSkuList || [],
      }));
      setSearchProducts(mapped);
    } catch (_) {
      setSearchProducts([]);
    }
  };

  // 處理目標倉庫庫位更新
  const handleTargetWarehouseChange = (itemIndex: number, warehouseId: string) => {
    const newItems = [...items];
    newItems[itemIndex] = {
      ...newItems[itemIndex],
      targetWarehouse: warehouseId,
      toLocation: '' // 重置庫位選擇
    };
    setItems(newItems);
    // 懶載入庫位
    if (warehouseId && !dynamicLocationsByWarehouse[warehouseId]) {
      (async () => {
        try {
          // 優先用倉庫 code 查詢，以提高命中率
          const allWh = [...warehouses, ...localWarehouses];
          const wh = allWh.find(w => String(w.value) === String(warehouseId));
          const queryKey = (wh?.code && String(wh.code).trim().length > 0) ? String(wh.code) : String(warehouseId);
          const resp: any = await apiService.getWarehouseLocations(queryKey, { flat: true });
          const rows: any[] = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
          const locs = rows.map((r: any) => r.location_code || r.code || r.locationName || r.name).filter(Boolean);
          setDynamicLocationsByWarehouse((prev) => ({ ...prev, [warehouseId]: locs }));
        } catch (e) {
          setDynamicLocationsByWarehouse((prev) => ({ ...prev, [warehouseId]: [] }));
        }
      })();
    }
  };

  // 打開庫位下拉時，若尚未載入則嘗試載入
  const ensureLocationsLoaded = async (warehouseId?: string) => {
    const key = warehouseId ? String(warehouseId) : '';
    if (!key || dynamicLocationsByWarehouse[key]) return;
    try {
      const allWh = [...warehouses, ...localWarehouses];
      const wh = allWh.find(w => String(w.value) === key);
      const queryKey = (wh?.code && String(wh.code).trim().length > 0) ? String(wh.code) : key;
      const resp: any = await apiService.getWarehouseLocations(queryKey, { flat: true });
      const rows: any[] = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
      const locs = rows.map((r: any) => r.location_code || r.code || r.locationName || r.name).filter(Boolean);
      setDynamicLocationsByWarehouse((prev) => ({ ...prev, [key]: locs }));
    } catch (_) {
      setDynamicLocationsByWarehouse((prev) => ({ ...prev, [key]: [] }));
    }
  };

  // 獲取可用庫存
  const getAvailableStock = (productId: string, warehouseId: string): number | undefined => {
    const product = products.find(p => p.id === productId);
    if (!product) return undefined;
    const val = (product.availableStock || ({} as any))[warehouseId];
    if (val === undefined || val === null) return undefined;
    const num = Number(val);
    return Number.isFinite(num) ? num : undefined;
  };

  // 添加商品行項目
  const addItem = () => {
    const newItem: TransferItem = {
      id: `item_${Date.now()}`,
      productId: '',
      productName: '',
      productNameCN: '',
      sku: '',
      quantity: 1,
      availableQuantity: undefined as any,
      unit: '個',
      fromLocation: '',
      toLocation: '',
      isQuantityValid: true,
      currentCustomer: '',
      targetCustomer: '',
      currentWarehouse: '',
      targetWarehouse: ''
    };
    setItems([...items, newItem]);
  };

  // 刪除商品行項目
  const removeItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  // 更新商品行項目
  const updateItem = (index: number, field: keyof TransferItem, value: any) => {
    const newItems = [...items];
    // 轉移數量加上邊界控制與即時校驗
    if (field === 'quantity') {
      const availRaw = newItems[index].availableQuantity;
      const available = typeof availRaw === 'number' ? availRaw : undefined;
      const raw = Number(value || 1);
      const clamped = Math.max(1, available !== undefined ? Math.min(raw, available) : raw);
      newItems[index] = { ...newItems[index], quantity: clamped };
      newItems[index].isQuantityValid = available !== undefined ? clamped <= available : true;
    } else {
      if (field === 'productName') {
        const prevCN = newItems[index].productNameCN || '';
        newItems[index] = {
          ...newItems[index],
          productName: String(value || ''),
          productNameCN: prevCN ? prevCN : String(value || ''),
          sku: newItems[index].sku || '',
          availableQuantity: newItems[index].availableQuantity,
          isQuantityValid: true,
        } as any;
      } else {
        newItems[index] = { ...newItems[index], [field]: value } as any;
      }
    }
    
    setItems(newItems);
  };

  // 處理商品選擇
  const handleProductSelect = (index: number, value: string, option?: any) => {
    console.log('Product selection triggered:', { index, value, option });
    
    // 優先從option中獲取product數據，如果沒有則通過value查找
    let product = option?.product;
    if (!product) {
      product = products.find(p => 
        p.id === value || 
        p.name === value || 
        p.sku === value ||
        p.nameCN === value
      );
    }
    
    console.log('Found product:', product);
    
    if (product) {
      // 自動填充當前位置信息（從產品的當前位置）；若缺失，從 availableStock 估算來源倉庫
      const currentLocation = product.currentLocation;
      let currentWarehouse = '';
      let currentLocationName = '';
      let currentCustomer = '';
      let availableQuantity: number | undefined = undefined;

      if (currentLocation && currentLocation.warehouse) {
        currentWarehouse = currentLocation.warehouse;
        currentLocationName = currentLocation.location;
        currentCustomer = currentLocation.customer || '';
        availableQuantity = getAvailableStock(product.id, currentWarehouse);
      }

      // 若沒有當前庫位信息或無法取得可用量，嘗試從可用庫存映射推斷
      if (availableQuantity === undefined) {
        const entries = Object.entries(product.availableStock || {});
        if (entries.length > 0) {
          // 優先選擇有正庫存的倉庫；否則選第一個鍵
          const positive = entries.find(([, qty]) => Number(qty) > 0);
          const fallback = positive || entries[0];
          if (fallback) {
            const [whId, qty] = fallback;
            currentWarehouse = currentWarehouse || String(whId);
            availableQuantity = Number(qty);
          }
        }
        // 若選了發送倉庫，優先使用該倉庫的可用量
        if (fromWarehouse) {
          const qty = getAvailableStock(product.id, fromWarehouse);
          if (typeof qty === 'number') {
            currentWarehouse = fromWarehouse;
            availableQuantity = qty;
          }
        }
      }

      console.log('Current location info:', { currentWarehouse, currentLocationName, currentCustomer, availableQuantity });

      // 批量更新商品信息
      const newItems = [...items];
      newItems[index] = {
        ...newItems[index],
        productId: product.id,
        productName: product.name,
        productNameCN: product.nameCN,
        sku: product.sku,
        unit: product.unit,
        availableQuantity: availableQuantity,
        currentCustomer: currentCustomer,
        currentWarehouse: currentWarehouse,
        fromLocation: currentLocationName,
        isQuantityValid: (availableQuantity === undefined) ? true : ((newItems[index].quantity || 1) <= availableQuantity)
      };

      setItems(newItems);
      console.log('Updated items:', newItems);
    } else {
      // 若找不到產品，作為自由輸入商品處理
      const newItems = [...items];
      newItems[index] = {
        ...newItems[index],
        productId: newItems[index].productId || `manual_${Date.now()}`,
        productName: String(value || ''),
        productNameCN: String(value || ''),
        sku: newItems[index].sku || '',
        unit: newItems[index].unit || '個',
        availableQuantity: newItems[index].availableQuantity,
        currentCustomer: newItems[index].currentCustomer || '',
        currentWarehouse: newItems[index].currentWarehouse || '',
        fromLocation: newItems[index].fromLocation || '',
        isQuantityValid: true,
      } as any;
      setItems(newItems);
    }
  };

  // 產品搜索選項
  const getProductOptions = () => {
    const source = (searchProducts.length > 0 ? searchProducts : products);
    return source.map(product => ({
      value: product.name,
      label: `${product.name} (${product.sku}) - ${product.nameCN}${product.alternateSkuList?.length ? ` [${product.alternateSkuList.join(', ')}]` : ''}`,
      product: product,
      key: product.id
    }));
  };

  // 發送倉庫變更：更新頂層狀態，對尚未確定來源倉庫的行進行回填
  const handleFromWarehouseChange = (warehouseId: string) => {
    setFromWarehouse(warehouseId);
    if (!warehouseId) return;
    const newItems = items.map((it) => {
      // 僅在當前倉庫未設定時回填，並嘗試刷新可用量
      const currentWarehouse = it.currentWarehouse || warehouseId;
      const refreshedQty = it.productId ? getAvailableStock(it.productId, currentWarehouse) : it.availableQuantity;
      return {
        ...it,
        currentWarehouse,
        availableQuantity: typeof refreshedQty === 'number' ? refreshedQty : it.availableQuantity,
      } as TransferItem;
    });
    setItems(newItems);
  };

  // 提交表單
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (items.length === 0) {
        message.error('請至少添加一個商品');
        return;
      }

      // 檢查每個商品是否選擇了產品和目標倉庫
      const incompleteItems = items.filter(item => 
        !item.productName || !item.targetWarehouse || !item.toLocation
      );
      if (incompleteItems.length > 0) {
        message.error(`有 ${incompleteItems.length} 個商品未完成設定，請檢查產品選擇、目標倉庫和目標庫位`);
        return;
      }

      // 檢查是否有庫存問題
      const stockIssues = items.filter(item => !item.isQuantityValid);
      if (stockIssues.length > 0) {
        Modal.confirm({
          title: '庫存警告',
          content: `有 ${stockIssues.length} 個商品庫存不足，是否繼續提交？`,
          onOk: () => {
            submitTransfer(values);
          },
        });
      } else {
        submitTransfer(values);
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const submitTransfer = (values: any) => {
    const totalItems = items.length;
    const totalQuantity = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    const estimatedValue = items.reduce((sum, item) => {
      const product = products.find(p => p.id === item.productId);
      return sum + ((Number(item.quantity) || 0) * 100); // 假設單價為100，實際應從產品數據獲取
    }, 0);

    // 統計涉及的倉庫
    const involvedWarehouses = new Set();
    items.forEach(item => {
      if (item.currentWarehouse) involvedWarehouses.add(item.currentWarehouse);
      if (item.targetWarehouse) involvedWarehouses.add(item.targetWarehouse);
    });

    const transferData = {
      ...values,
      items: items.map(it => ({
        id: it.id,
        productId: it.productId || undefined,
        productName: it.productName,
        productNameCN: it.productNameCN,
        sku: it.sku,
        quantity: it.quantity,
        availableQuantity: it.availableQuantity,
        unit: it.unit,
        fromLocation: it.fromLocation,
        toLocation: it.toLocation,
        isQuantityValid: it.isQuantityValid,
        remark: (it as any).remark,
        targetCustomerId: it.targetCustomer || undefined,
        targetCustomer: undefined,
        currentCustomer: undefined,
        currentWarehouse: undefined,
        targetWarehouse: it.targetWarehouse || undefined,
      })),
      totalItems,
      totalQuantity,
      estimatedValue,
      involvedWarehouses: Array.from(involvedWarehouses),
      status: '待審批' as TransferStatus
    };

    onSubmit(transferData);
  };

  // 商品表格列定義
  const itemColumns: ColumnsType<TransferItem> = [
    {
      title: '商品名稱',
      key: 'productName',
      width: 200,
      render: (_, record, index) => (
        <AutoComplete
          style={{ width: '100%' }}
          options={getProductOptions()}
          value={record.productName}
          onSelect={(value, option) => handleProductSelect(index, value, option)}
          onChange={(value) => updateItem(index, 'productName', value)}
          placeholder="搜尋商品名稱或SKU"
          onSearch={(text) => searchProductsByKeyword(text)}
          onDropdownVisibleChange={(open) => { if (open && searchProducts.length === 0) searchProductsByKeyword(); }}
          filterOption={(inputValue, option) => {
            if (!inputValue || !option) return true;
            const input = inputValue.toLowerCase();
            const product = option.product;
            return (
              option.label.toLowerCase().includes(input) ||
              product.name.toLowerCase().includes(input) ||
              product.nameCN.toLowerCase().includes(input) ||
              product.sku.toLowerCase().includes(input)
            );
          }}
          allowClear
        />
      ),
    },
    {
      title: '中文名稱',
      key: 'productNameCN',
      width: 150,
      render: (_, record) => (
        <div>
          <div>{record.productNameCN}</div>
          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
            SKU: {record.sku}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: '當前客戶',
      key: 'currentCustomer',
      width: 120,
      render: (_, record) => (
        <div>
          <Typography.Text style={{ fontSize: '12px' }}>
            {record.currentCustomer || '無'}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: '當前位置',
      key: 'currentLocation',
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            <HomeOutlined style={{ marginRight: 4 }} />
            {record.currentWarehouse && 
              warehouses.find(w => w.value === record.currentWarehouse)?.label
            }
          </div>
          {record.fromLocation && (
            <Typography.Text type="secondary" style={{ fontSize: '11px' }}>
              {record.fromLocation}
            </Typography.Text>
          )}
        </div>
      ),
    },
    {
      title: '轉移數量',
      key: 'quantity',
      width: 120,
      render: (_, record, index) => (
        <div>
          <InputNumber
            min={1}
            max={record.availableQuantity || undefined}
            value={record.quantity}
            onChange={(value) => updateItem(index, 'quantity', value || 1)}
            style={{ width: '100%' }}
            status={record.isQuantityValid ? undefined : 'error'}
            addonAfter={record.unit}
          />
          {!record.isQuantityValid && typeof record.availableQuantity === 'number' && (
            <div style={{ fontSize: '12px', color: '#ff4d4f' }}>
              可用: {record.availableQuantity}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '目標客戶',
      key: 'targetCustomer',
      width: 120,
      render: (_, record, index) => (
        <Select
          value={record.targetCustomer}
          onChange={(value) => updateItem(index, 'targetCustomer', value)}
          placeholder="選擇目標客戶"
          style={{ width: '100%' }}
          allowClear
          showSearch
          optionFilterProp="children"
          notFoundContent="無客戶"
          onDropdownVisibleChange={(open) => { if (open) ensureCustomersLoaded(); }}
        >
          {((customers?.length || 0) + (localCustomers?.length || 0)) === 0 && (
            <Option key="_empty" value="" disabled>
              無客戶
            </Option>
          )}
          {(customers && customers.length > 0 ? customers : localCustomers).map(customer => (
            <Option key={customer.id} value={customer.id}>
              {customer.name} ({customer.code})
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: '目標倉庫',
      key: 'targetWarehouse',
      width: 120,
      render: (_, record, index) => (
        <Select
          value={record.targetWarehouse}
          onChange={(value) => handleTargetWarehouseChange(index, value)}
          placeholder="選擇目標倉庫"
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="children"
          notFoundContent="無倉庫"
        >
          {warehouses.map(warehouse => (
            <Option key={warehouse.value} value={warehouse.value}>
              <Space>
                <HomeOutlined />
                {warehouse.label}
              </Space>
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: '目標庫位',
      key: 'toLocation',
      width: 120,
      render: (_, record, index) => (
        <Select
          value={record.toLocation}
          onChange={(value) => updateItem(index, 'toLocation', value)}
          placeholder="選擇目標庫位"
          style={{ width: '100%' }}
          disabled={!record.targetWarehouse}
          showSearch
          optionFilterProp="children"
          onDropdownVisibleChange={(open) => { if (open) ensureLocationsLoaded(record.targetWarehouse); }}
          notFoundContent={record.targetWarehouse ? '無庫位' : undefined}
        >
          {!record.targetWarehouse && (
            <Option key="_select_wh" value="" disabled>
              請先選擇目標倉庫
            </Option>
          )}
          {record.targetWarehouse &&
            ((dynamicLocationsByWarehouse[record.targetWarehouse] || warehouses.find(w => w.value === record.targetWarehouse)?.locations || [])).map(location => (
              <Option key={location} value={location}>{location}</Option>
            ))}
        </Select>
      ),
    },
    {
      title: '備註',
      key: 'remark',
      width: 150,
      render: (_, record, index) => (
        <Input
          value={record.remark}
          onChange={(e) => updateItem(index, 'remark', e.target.value)}
          placeholder="備註"
        />
      ),
    },
    {
      title: '狀態',
      key: 'status',
      width: 80,
      render: (_, record) => (
        record.isQuantityValid ? 
          <Tag color="green" icon={<CheckCircleOutlined />}>正常</Tag> : 
          <Tag color="red" icon={<WarningOutlined />}>庫存不足</Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      fixed: 'right' as const,
      render: (_, record, index) => (
        <Popconfirm
          title="確定要刪除此商品嗎？"
          onConfirm={() => removeItem(index)}
          okText="確定"
          cancelText="取消"
        >
          <Button type="link" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <SwapOutlined />
          {transfer ? '編輯轉移請求' : '新增轉移請求'}
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={1600}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          取消
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          {transfer ? '更新' : '創建'}
        </Button>,
      ]}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          priority: 'medium',
          status: '待審批',
        }}
      >
        <Alert
          message="轉移請求說明"
          description="請填寫轉移請求的基本信息，選擇源倉庫和目標倉庫，然後添加需要轉移的商品。系統會自動檢查庫存可用性。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {/* 基本信息 */}
        <Card title="基本信息" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="轉移標題"
                name="title"
                rules={[{ required: true, message: '請輸入轉移標題' }]}
              >
                <Input placeholder="請描述此次轉移的目的" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="優先級"
                name="priority"
                rules={[{ required: true, message: '請選擇優先級' }]}
              >
                <Select>
                  <Option value="urgent">緊急</Option>
                  <Option value="high">高</Option>
                  <Option value="medium">中</Option>
                  <Option value="low">低</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="發送倉庫" name="fromWarehouse">
                <Select
                  placeholder="選擇發送倉庫"
                  value={fromWarehouse}
                  onChange={handleFromWarehouseChange}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  onDropdownVisibleChange={(open) => { if (open) ensureWarehousesLoaded(); }}
                >
                  {(warehouses.length > 0 ? warehouses : localWarehouses).map((w) => (
                    <Option key={w.value} value={w.value}>{w.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="目標倉庫" name="toWarehouse">
                <Select
                  placeholder="選擇目標倉庫"
                  value={toWarehouse}
                  onChange={setToWarehouse}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  onDropdownVisibleChange={(open) => { if (open) ensureWarehousesLoaded(); }}
                >
                  {(warehouses.length > 0 ? warehouses : localWarehouses).map((w) => (
                    <Option key={w.value} value={w.value}>{w.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="轉移原因"
                name="reason"
                rules={[{ required: true, message: '請填寫轉移原因' }]}
              >
                <TextArea rows={2} placeholder="請詳細說明此次轉移的原因和目的" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* 工作流程說明 */}
        <Card title="轉移工作流程" style={{ marginBottom: 16 }}>
          <Alert
            message="新的轉移流程"
            description={
              <div>
                <div style={{ marginBottom: 8 }}>請按以下順序操作：</div>
                <div>1. <strong>選擇產品</strong> - 搜尋並選擇要轉移的產品</div>
                <div>2. <strong>查看當前位置</strong> - 系統自動顯示產品當前的客戶和倉庫位置</div>
                <div>3. <strong>選擇目標位置</strong> - 選擇目標客戶、目標倉庫和庫位</div>
                <div>4. <strong>設定數量</strong> - 輸入要轉移的數量</div>
              </div>
            }
            type="info"
            showIcon
          />
        </Card>

        {/* 商品清單 */}
        <Card 
          title={
            <Space>
              <BoxPlotOutlined />
              轉移商品清單
              {items.length > 0 && (
                <Tag color="blue">{items.length} 種商品</Tag>
              )}
            </Space>
          }
          extra={
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={addItem}
            >
              添加商品
            </Button>
          }
          style={{ marginBottom: 16 }}
        >
          {items.length === 0 ? (
            <Alert
              message="尚未添加商品"
              description="請點擊「添加商品」按鈕來添加需要轉移的商品"
              type="warning"
              showIcon
            />
          ) : (
            <Table
              dataSource={items}
              columns={itemColumns}
              rowKey="id"
              pagination={false}
              scroll={{ x: 1400 }}
              size="small"
            />
          )}

          {items.length > 0 && (
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Space size="large">
                <Typography.Text>
                  總商品種類: <Tag color="blue">{items.length}</Tag>
                </Typography.Text>
                <Typography.Text>
                  總轉移數量: <Tag color="green">{items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)}</Tag>
                </Typography.Text>
                <Typography.Text>
                  庫存問題: <Tag color={items.some(item => !item.isQuantityValid) ? "red" : "green"}>
                    {items.filter(item => !item.isQuantityValid).length} 種商品
                  </Tag>
                </Typography.Text>
              </Space>
            </div>
          )}
        </Card>

        {/* 備註 */}
        <Card title="備註信息">
          <Form.Item
            label="備註"
            name="notes"
          >
            <TextArea rows={3} placeholder="其他備註信息..." />
          </Form.Item>
        </Card>
      </Form>
    </Modal>
  );
};

export default InternalTransferForm;